import { useEffect, useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { profileService, videoService, type PublicProfile } from '../../services'
import type { Video, SocialLinks } from '../../types/database.types'
import { VideoGrid } from '../../components/video'
import { Button, LoadingScreen } from '../../components/common'

// Social platform icons configuration
const SOCIAL_ICONS: Record<keyof SocialLinks, { label: string; icon: React.ReactNode }> = {
  twitter: {
    label: 'Twitter / X',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  linkedin: {
    label: 'LinkedIn',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  instagram: {
    label: 'Instagram',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  facebook: {
    label: 'Facebook',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  youtube: {
    label: 'YouTube',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  tiktok: {
    label: 'TikTok',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
  },
  threads: {
    label: 'Threads',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.025.855-.71 2.025-1.12 3.476-1.22 1.072-.073 2.074-.02 3.006.158-.015-1.478-.353-2.574-1.011-3.266-.715-.752-1.835-1.135-3.329-1.14h-.035c-1.184.005-2.15.324-2.87.948l-1.321-1.605c1.073-.932 2.49-1.42 4.21-1.452h.046c2.065.01 3.666.585 4.76 1.707.99 1.018 1.53 2.478 1.605 4.342.513.18.996.404 1.446.67 1.249.737 2.178 1.78 2.685 3.017.747 1.822.745 4.574-1.453 6.723-1.863 1.822-4.168 2.632-7.272 2.654zm-1.123-8.144c-.934.064-1.665.272-2.172.618-.454.31-.68.695-.656 1.117.041.737.59 1.591 2.118 1.591.096 0 .193-.004.292-.012 1.122-.066 1.94-.483 2.43-1.24.394-.605.583-1.395.575-2.35-.652-.131-1.4-.184-2.238-.13l-.349.026z" />
      </svg>
    ),
  },
}

// Helper to render social links
function SocialLinksRow({ links }: { links: SocialLinks | null }) {
  if (!links) return null

  const activePlatforms = (Object.keys(links) as (keyof SocialLinks)[]).filter(
    (key) => links[key]
  )

  if (activePlatforms.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {activePlatforms.map((platform) => {
        const config = SOCIAL_ICONS[platform]
        const url = links[platform]
        if (!config || !url) return null

        return (
          <a
            key={String(platform)}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
            title={config.label}
          >
            {config.icon}
          </a>
        )
      })}
    </div>
  )
}

export function ProviderProfilePage() {
  const { id } = useParams<{ id: string }>()

  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [videos, setVideos] = useState<Video[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showContactModal, setShowContactModal] = useState(false)

  useEffect(() => {
    const loadProfile = async () => {
      if (!id) return

      setIsLoading(true)
      try {
        const [profileData, videosData] = await Promise.all([
          profileService.getPublicProfile(id),
          videoService.getVideosByProvider(id),
        ])

        setProfile(profileData)
        setVideos(videosData)
      } catch (error) {
        console.error('Failed to load profile:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadProfile()
  }, [id])

  // Get unique categories from videos
  const categories = useMemo(() => {
    const cats = [...new Set(videos.map((v) => v.category).filter(Boolean))]
    return cats as string[]
  }, [videos])

  // Filter videos by category
  const filteredVideos = useMemo(() => {
    if (!selectedCategory) return videos
    return videos.filter((v) => v.category === selectedCategory)
  }, [videos, selectedCategory])

  // Get featured video (most viewed)
  const featuredVideo = useMemo(() => {
    if (videos.length === 0) return null
    return videos.reduce((prev, current) =>
      (current.view_count || 0) > (prev.view_count || 0) ? current : prev
    )
  }, [videos])

  // Calculate stats
  const stats = useMemo(() => ({
    totalVideos: videos.length,
    totalViews: videos.reduce((sum, v) => sum + (v.view_count || 0), 0),
    totalLikes: videos.reduce((sum, v) => sum + (v.like_count || 0), 0),
    avgDuration: videos.length > 0
      ? Math.round(videos.reduce((sum, v) => sum + (v.duration_seconds || 0), 0) / videos.length)
      : 0,
  }), [videos])

  if (isLoading) {
    return <LoadingScreen message="Loading profile..." />
  }

  if (!profile) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h1>
          <p className="text-gray-600 mb-6">
            This provider's profile is private or doesn't exist.
          </p>
          <Link to="/videos">
            <Button>Browse Videos</Button>
          </Link>
        </div>
      </div>
    )
  }

  // Convert videos to VideoWithProvider format for VideoGrid
  const videosWithProvider = filteredVideos.map((v) => ({
    ...v,
    user: {
      id: profile.id,
      full_name: profile.full_name,
      avatar_url: profile.avatar_url,
      specialty: profile.specialty,
      bio: profile.bio,
    },
  }))

  return (
    <div>
      {/* Hero Section with Cover */}
      <section className="relative">
        {/* Cover gradient */}
        <div className="h-48 md:h-64 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800" />

        {/* Profile card overlapping cover */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative -mt-24 pb-8">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="w-28 h-28 md:w-36 md:h-36 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                      {profile.avatar_url ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.full_name || 'Provider'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-4xl md:text-5xl font-bold text-blue-600">
                          {profile.full_name?.[0]?.toUpperCase() || 'P'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                          {profile.full_name || 'Provider'}
                        </h1>
                        {profile.specialty && (
                          <p className="text-lg text-blue-600 font-medium mt-1">
                            {profile.specialty}
                          </p>
                        )}
                        {profile.company_name && (
                          <p className="text-gray-600 flex items-center gap-1 mt-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            {profile.company_name}
                          </p>
                        )}
                        {profile.location && (
                          <p className="text-gray-500 flex items-center gap-1 mt-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {profile.location}
                          </p>
                        )}
                        {/* Social Links */}
                        {profile.social_links && Object.keys(profile.social_links).some(k => profile.social_links?.[k as keyof SocialLinks]) && (
                          <div className="mt-3">
                            <SocialLinksRow links={profile.social_links} />
                          </div>
                        )}
                      </div>

                      {/* CTA Buttons */}
                      <div className="flex gap-3">
                        <Button onClick={() => setShowContactModal(true)}>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Contact
                        </Button>
                        {profile.website_url && (
                          <a
                            href={profile.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Website
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Bio */}
                    {profile.bio && (
                      <p className="mt-4 text-gray-600 leading-relaxed max-w-2xl">
                        {profile.bio}
                      </p>
                    )}

                    {/* Stats Row */}
                    <div className="mt-6 flex flex-wrap items-center gap-6 pt-6 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{stats.totalVideos}</p>
                          <p className="text-xs text-gray-500">Videos</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{stats.totalViews.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">Views</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{stats.totalLikes.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">Likes</p>
                        </div>
                      </div>
                      {categories.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-xl font-bold text-gray-900">{categories.length}</p>
                            <p className="text-xs text-gray-500">Categories</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Video */}
      {featuredVideo && videos.length > 1 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Featured Video</h2>
          <Link
            to={`/videos/${featuredVideo.id}`}
            className="block bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="flex flex-col md:flex-row">
              <div className="md:w-2/5 aspect-video md:aspect-auto bg-gray-200 relative">
                {featuredVideo.thumbnail_url ? (
                  <img
                    src={featuredVideo.thumbnail_url}
                    alt={featuredVideo.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                </div>
                {featuredVideo.duration_seconds && (
                  <span className="absolute bottom-3 right-3 bg-black/75 text-white text-sm px-2 py-1 rounded">
                    {Math.floor(featuredVideo.duration_seconds / 60)}:{String(featuredVideo.duration_seconds % 60).padStart(2, '0')}
                  </span>
                )}
              </div>
              <div className="md:w-3/5 p-6">
                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded mb-2">
                  Most Popular
                </span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {featuredVideo.title}
                </h3>
                {featuredVideo.description && (
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {featuredVideo.description}
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {featuredVideo.view_count || 0} views
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                    {featuredVideo.like_count || 0} likes
                  </span>
                  {featuredVideo.category && (
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      {featuredVideo.category}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Videos Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            All Videos {selectedCategory && `in ${selectedCategory}`}
          </h2>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  !selectedCategory
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All ({videos.length})
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat} ({videos.filter((v) => v.category === cat).length})
                </button>
              ))}
            </div>
          )}
        </div>

        <VideoGrid
          videos={videosWithProvider}
          showProvider={false}
          emptyMessage={
            selectedCategory
              ? `No videos in ${selectedCategory} category.`
              : "This provider hasn't uploaded any public videos yet."
          }
        />
      </section>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowContactModal(false)}
            />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Contact {profile.full_name || 'Provider'}
              </h3>
              <p className="text-gray-600 mb-6">
                Interested in working with this provider? Get in touch!
              </p>

              <div className="space-y-4">
                {profile.website_url && (
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Visit Website</p>
                      <p className="text-sm text-gray-500 truncate">{profile.website_url}</p>
                    </div>
                  </a>
                )}

                {/* Social Links in Modal */}
                {profile.social_links && Object.keys(profile.social_links).some(k => profile.social_links?.[k as keyof SocialLinks]) && (
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-700 mb-3">Connect on social media</p>
                    <SocialLinksRow links={profile.social_links} />
                  </div>
                )}

                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Create a free account to save this provider
                    and get notified when they upload new videos.
                  </p>
                  <Link
                    to="/register"
                    className="mt-3 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Create account &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
